import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import HrDashboardLayout from '../components/layouts/HrDashboardLayout';
import Icon from '../components/atoms/Icon';
import Button from '../components/atoms/Button';
import SessionManager from '../utils/sessionManager';
import { getMessages, getConversations, sendMessage, markMessagesAsRead, deleteConversation } from '../services/messaging';
import { getApplications } from '../services/api';

const HrMessagesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [allCandidates, setAllCandidates] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const getHrCompanyName = () => {
    SessionManager.migrateLegacySession();
    const hrSession = SessionManager.getSession('hr');
    return hrSession?.company || hrSession?.profile?.company_name || 'Company';
  };
  const companyName = getHrCompanyName();

  // Auth check
  useEffect(() => {
    const isLoggedInAsHr = SessionManager.isLoggedIn('hr');
    const currentSession = SessionManager.getCurrentSession();
    if (!isLoggedInAsHr || (currentSession && currentSession.role !== 'hr')) {
      navigate('/hr_login');
    }
  }, [navigate]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when conversation selected
  useEffect(() => {
    if (selectedConversation) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [selectedConversation]);

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch all apps (for search) and conversations metadata in parallel — just 2 API calls
      const [allApplicationsData, allConvos] = await Promise.all([
        getApplications(),
        getConversations(),
      ]);
      setAllCandidates(allApplicationsData);

      // Build lookup of company apps
      const companyApps = allApplicationsData.filter(a =>
        a.company?.toLowerCase() === companyName?.toLowerCase()
      );
      const appMap = new Map(companyApps.map(a => [a.id, a]));

      // Merge conversation metadata with app data — no per-app API calls
      const filtered = allConvos
        .filter(c => appMap.has(c.application_id) && c.total_messages > 0)
        .map(c => {
          const app = appMap.get(c.application_id);
          return {
            ...app,
            unread_count: c.unread_count || 0,
            last_message: c.last_message || '',
            last_message_sender: c.last_message_sender || '',
            last_message_time: c.last_message_time ? new Date(c.last_message_time) : null,
            has_messages: true,
          };
        })
        .sort((a, b) => {
          if (a.unread_count !== b.unread_count) return b.unread_count - a.unread_count;
          return (b.last_message_time || 0) - (a.last_message_time || 0);
        });

      setConversations(filtered);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [companyName]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // Handle pre-selected application from navigation
  useEffect(() => {
    if (location.state?.selectedApplication && conversations.length > 0) {
      const app = location.state.selectedApplication;
      const conv = conversations.find(c => c.id === app.id);
      if (conv) setSelectedConversation(conv);
    }
  }, [location.state, conversations]);

  const loadMessages = useCallback(async (applicationId) => {
    const data = await getMessages(applicationId);
    setMessages(data);
    await markMessagesAsRead(applicationId, 'Candidate');
  }, []);

  useEffect(() => {
    if (selectedConversation) loadMessages(selectedConversation.id);
  }, [selectedConversation, loadMessages]);

  // Messages loaded once when conversation is selected (no polling)

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !selectedConversation || sending) return;

    const text = newMessage.trim();
    setNewMessage('');
    setSending(true);

    try {
      const hrName = companyName + ' HR Team';
      const msg = await sendMessage(selectedConversation.id, 'HR', text, hrName);

      if (msg) {
        // Optimistic update
        setMessages(prev => [...prev, msg]);

        // Update conversation list
        setConversations(prev => {
          const exists = prev.find(c => c.id === selectedConversation.id);
          if (exists) {
            return prev.map(c =>
              c.id === selectedConversation.id
                ? { ...c, last_message: text, last_message_sender: 'HR', last_message_time: new Date() }
                : c
            );
          }
          return [{ ...selectedConversation, has_messages: true, last_message: text, last_message_sender: 'HR', last_message_time: new Date(), unread_count: 0 }, ...prev];
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const confirmDeleteConversation = async () => {
    if (!deleteConfirm) return;
    await deleteConversation(deleteConfirm);
    if (selectedConversation?.id === deleteConfirm) {
      setSelectedConversation(null);
      setMessages([]);
    }
    setConversations(prev => prev.filter(c => c.id !== deleteConfirm));
    setDeleteConfirm(null);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim()) {
      const q = query.toLowerCase();
      const results = allCandidates.filter(c =>
        c.candidate_name?.toLowerCase().includes(q) ||
        c.candidate_email?.toLowerCase().includes(q) ||
        c.job_title?.toLowerCase().includes(q)
      );
      setSearchResults(results);
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  const startConversation = (candidate) => {
    setSelectedConversation(candidate);
    setSearchQuery('');
    setShowSearchResults(false);
    loadMessages(candidate.id);
  };

  // Format time like WhatsApp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const d = new Date(timestamp);
    const now = new Date();
    const diff = now - d;
    const hours = d.getHours();
    const mins = d.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const h = hours % 12 || 12;
    const time = `${h}:${mins} ${ampm}`;

    if (diff < 86400000 && now.getDate() === d.getDate()) return time;
    if (diff < 172800000) return `Yesterday ${time}`;
    return `${d.getDate()}/${d.getMonth() + 1} ${time}`;
  };

  // Group messages by date
  const groupMessagesByDate = (msgs) => {
    const groups = {};
    msgs.forEach(msg => {
      const d = new Date(msg.timestamp);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      let label;
      if (d.toDateString() === today.toDateString()) label = 'Today';
      else if (d.toDateString() === yesterday.toDateString()) label = 'Yesterday';
      else label = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

      if (!groups[label]) groups[label] = [];
      groups[label].push(msg);
    });
    return groups;
  };

  if (loading) {
    return (
      <HrDashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </HrDashboardLayout>
    );
  }

  const messageGroups = groupMessagesByDate(messages);

  return (
    <HrDashboardLayout>
      <div className="h-full flex flex-col">
        {/* Full-height chat container */}
        <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex" style={{ height: 'calc(100vh - 120px)' }}>

          {/* ======= LEFT PANEL: Conversations ======= */}
          <div className="w-[340px] border-r border-gray-200 flex flex-col bg-white">
            {/* Header */}
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">Chats</h2>
            </div>

            {/* Search */}
            <div className="px-3 py-2 bg-gray-50">
              <div className="relative">
                <Icon name="search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search or start new chat"
                  className="w-full pl-9 pr-8 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                />
                {searchQuery && (
                  <button onClick={() => { setSearchQuery(''); setShowSearchResults(false); }} className="absolute right-2 top-1/2 -translate-y-1/2">
                    <Icon name="close" size={16} className="text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>
            </div>

            {/* Conversation list */}
            <div className="flex-1 overflow-y-auto">
              {showSearchResults ? (
                searchResults.length === 0 ? (
                  <div className="p-6 text-center">
                    <Icon name="search_off" size={40} className="text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No candidates found</p>
                  </div>
                ) : (
                  <>
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
                      {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                    </div>
                    {searchResults.map((c) => (
                      <div key={c.id} onClick={() => startConversation(c)} className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 transition-colors">
                        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                          {c.candidate_name?.charAt(0).toUpperCase() || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{c.candidate_name || 'Unknown'}</p>
                          <p className="text-xs text-gray-500 truncate">{c.job_title}</p>
                        </div>
                      </div>
                    ))}
                  </>
                )
              ) : conversations.length === 0 ? (
                <div className="p-8 text-center">
                  <Icon name="chat_bubble_outline" size={48} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No conversations yet</p>
                  <p className="text-xs text-gray-400 mt-1">Search candidates to start chatting</p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => setSelectedConversation(conv)}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-gray-100 transition-colors group relative ${
                      selectedConversation?.id === conv.id
                        ? 'bg-emerald-50 border-l-4 border-l-emerald-500'
                        : 'hover:bg-gray-50 border-l-4 border-l-transparent'
                    }`}
                  >
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center text-white font-semibold">
                        {conv.candidate_name?.charAt(0).toUpperCase() || '?'}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <p className="text-sm font-semibold text-gray-900 truncate pr-2">
                          {conv.candidate_name}
                        </p>
                        <span className="text-[11px] text-gray-400 flex-shrink-0">
                          {conv.last_message_time ? formatTime(conv.last_message_time) : ''}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500 truncate pr-2">
                          {conv.last_message_sender === 'HR' && (
                            <span className="text-gray-400">You: </span>
                          )}
                          {conv.last_message || 'No messages'}
                        </p>
                        {conv.unread_count > 0 && (
                          <span className="bg-emerald-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5 flex-shrink-0">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-400 truncate mt-0.5">{conv.job_title}</p>
                    </div>

                    {/* Delete on hover */}
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteConfirm(conv.id); }}
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full hover:bg-red-50"
                      title="Delete chat"
                    >
                      <Icon name="delete" size={14} className="text-red-400" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* ======= RIGHT PANEL: Chat ======= */}
          <div className="flex-1 flex flex-col bg-[#efeae2]">
            {selectedConversation ? (
              <>
                {/* Chat header */}
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center text-white font-semibold text-sm">
                      {selectedConversation.candidate_name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{selectedConversation.candidate_name}</p>
                      <p className="text-xs text-gray-500">{selectedConversation.job_title} {selectedConversation.candidate_email ? `\u00b7 ${selectedConversation.candidate_email}` : ''}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setDeleteConfirm(selectedConversation.id)}
                    className="p-2 rounded-full hover:bg-gray-200 transition-colors"
                    title="Clear chat"
                  >
                    <Icon name="delete" size={18} className="text-gray-500" />
                  </button>
                </div>

                {/* Messages area - WhatsApp style background */}
                <div className="flex-1 overflow-y-auto px-4 py-2" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'400\' height=\'400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23d5cfc4\' fill-opacity=\'0.15\'%3E%3Ccircle cx=\'50\' cy=\'50\' r=\'2\'/%3E%3Ccircle cx=\'150\' cy=\'100\' r=\'1.5\'/%3E%3Ccircle cx=\'250\' cy=\'50\' r=\'1\'/%3E%3Ccircle cx=\'350\' cy=\'150\' r=\'2\'/%3E%3Ccircle cx=\'100\' cy=\'200\' r=\'1.5\'/%3E%3Ccircle cx=\'200\' cy=\'250\' r=\'1\'/%3E%3Ccircle cx=\'300\' cy=\'300\' r=\'2\'/%3E%3Ccircle cx=\'50\' cy=\'350\' r=\'1.5\'/%3E%3C/g%3E%3C/svg%3E")' }}>
                  {messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="bg-white/80 backdrop-blur-sm rounded-lg px-6 py-4 shadow-sm text-center">
                        <Icon name="lock" size={16} className="text-yellow-600 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">Messages are stored securely. Start the conversation!</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      {Object.entries(messageGroups).map(([dateLabel, msgs]) => (
                        <div key={dateLabel}>
                          {/* Date separator */}
                          <div className="flex justify-center my-3">
                            <span className="bg-white/90 text-gray-600 text-[11px] font-medium px-3 py-1 rounded-md shadow-sm">
                              {dateLabel}
                            </span>
                          </div>

                          {/* Messages for this date */}
                          {msgs.map((msg) => (
                            <div key={msg.id} className={`flex mb-1 ${msg.sender === 'HR' ? 'justify-end' : 'justify-start'}`}>
                              <div
                                className={`relative max-w-[65%] px-3 py-2 rounded-lg shadow-sm ${
                                  msg.sender === 'HR'
                                    ? 'bg-[#d9fdd3] text-gray-900 rounded-tr-none'
                                    : 'bg-white text-gray-900 rounded-tl-none'
                                }`}
                              >
                                {/* Sender name for candidate */}
                                {msg.sender === 'Candidate' && msg.sender_name && (
                                  <p className="text-xs font-semibold text-emerald-700 mb-0.5">{msg.sender_name}</p>
                                )}
                                <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                                <div className="flex items-center justify-end gap-1 mt-0.5">
                                  <span className="text-[10px] text-gray-500">
                                    {formatTime(msg.timestamp)}
                                  </span>
                                  {msg.sender === 'HR' && (
                                    <span className="text-[10px]">
                                      {msg.read ? (
                                        <span className="text-blue-500">✓✓</span>
                                      ) : (
                                        <span className="text-gray-400">✓✓</span>
                                      )}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Input bar - WhatsApp style */}
                <form onSubmit={handleSendMessage} className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message"
                      className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                      disabled={sending}
                    />
                    <button
                      type="submit"
                      disabled={sending || !newMessage.trim()}
                      className="w-10 h-10 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 text-white rounded-full flex items-center justify-center transition-colors flex-shrink-0"
                    >
                      <Icon name="send" size={18} />
                    </button>
                  </div>
                </form>
              </>
            ) : (
              /* Empty state */
              <div className="flex-1 flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <div className="w-56 h-56 mx-auto mb-6 flex items-center justify-center">
                    <svg viewBox="0 0 303 172" className="w-full opacity-30">
                      <path d="M229.565 160.229c32.647-10.984 57.366-41.988 53.825-86.81C279.727 22.07 235.092-7.6 189.553 2.273 144.013 12.145 131.85 40.488 97.1 40.488c-34.75 0-59.6-22.375-83.1-10.875C-9.5 41.113-2.3 78.44 16.3 101.238c18.6 22.8 42.7 35.1 75.15 42.25 32.45 7.15 105.467 27.724 138.115 16.74Z" fill="currentColor" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-light text-gray-600 mb-2">Aptirix Messaging</h3>
                  <p className="text-sm text-gray-400">Select a conversation or search for a candidate to start chatting</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Delete confirmation modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setDeleteConfirm(null)}>
            <div className="bg-white rounded-xl p-5 max-w-sm w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-base font-semibold text-gray-900 mb-2">Delete this chat?</h3>
              <p className="text-sm text-gray-600 mb-5">All messages in this conversation will be permanently deleted.</p>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                  Cancel
                </button>
                <button onClick={confirmDeleteConversation} className="px-4 py-2 text-sm bg-red-500 text-white hover:bg-red-600 rounded-lg transition-colors">
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </HrDashboardLayout>
  );
};

export default HrMessagesPage;
